import axios from "axios";
import type { OEMProfile } from "../../types/Profiles/oemProfile.types";

const backendUrl =
  process.env.REACT_APP_BACKEND_URL;

const axiosInstance = axios.create({
  baseURL: `${backendUrl}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const normalizeOEMProfile = (data: any): OEMProfile => {
  return {
    identity: {
      companyName: data?.identity?.companyName || "",
      logo: data?.identity?.logo || "",
      country: data?.identity?.country || "",
      city: data?.identity?.city || "",
      headquarters: data?.identity?.headquarters || "",
      website: data?.identity?.website || "",
      contactEmail: data?.identity?.contactEmail || "",
      contactPhone: data?.identity?.contactPhone || "",
      description: data?.identity?.description || "",
      industry: data?.identity?.industry || "",
    },

    industrialPresence: {
      plants: Number(data?.industrialPresence?.plants || 0),
      assemblyLines: Number(data?.industrialPresence?.assemblyLines || 0),
      productionCapacity: Number(
        data?.industrialPresence?.productionCapacity || 0
      ),
      sopPrograms: Number(data?.industrialPresence?.sopPrograms || 0),
    },

    procurementSegments: Array.isArray(data?.procurementSegments)
      ? data.procurementSegments
      : [],

    rfqStats: {
      activeRFQ: Number(data?.rfqStats?.activeRFQ || 0),
      suppliersMatched: Number(data?.rfqStats?.suppliersMatched || 0),
      projectsInLocalization: Number(
        data?.rfqStats?.projectsInLocalization || 0
      ),
      sopReached: Number(data?.rfqStats?.sopReached || 0),
    },

    localIntegration: {
      localContentTarget: data?.localIntegration?.localContentTarget || "0%",
    },

    profileCompletion: {
      percentage: Number(data?.profileCompletion?.percentage || 0),
      items: Array.isArray(data?.profileCompletion?.items)
        ? data.profileCompletion.items
        : [],
    },

    timeline: Array.isArray(data?.timeline) ? data.timeline : [],
    history: Array.isArray(data?.history) ? data.history : [],
  };
};

const getCurrentUserId = (): number | null => {
  try {
    const rawUser = localStorage.getItem("user");
    if (!rawUser) return null;

    const user = JSON.parse(rawUser);
    return Number(user?.id_user) || null;
  } catch (error) {
    console.error("Erreur lecture localStorage user:", error);
    return null;
  }
};

/* ===========================
   FETCH OEM PROFILE
=========================== */
export const fetchOEMProfile = async (
  id_user: number | string
): Promise<OEMProfile | null> => {
  try {
    const response = await axiosInstance.get(`/oems/profile-view/${id_user}`);
    return normalizeOEMProfile(response.data);
  } catch (error) {
    console.error("Erreur fetchOEMProfile:", error);
    return null;
  }
};

/* ===========================
   FETCH CURRENT OEM PROFILE
=========================== */
export const fetchCurrentOEMProfile = async (): Promise<OEMProfile | null> => {
  const user_id = getCurrentUserId();

  if (!user_id) {
    throw new Error("id_user introuvable dans localStorage");
  }

  return await fetchOEMProfile(user_id);
};

/* ===========================
   UPDATE COMPANY
=========================== */
export const updateOEMCompany = async (payload: {
  companyName: string;
  industry: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  country: string;
  city: string;
  headquarters: string;
  description: string;
}): Promise<any> => {
  const user_id = getCurrentUserId();

  if (!user_id) {
    throw new Error("id_user introuvable dans localStorage");
  }

  const response = await axiosInstance.post("/oems/update-profile", {
    user_id,
    company_name: payload.companyName,
    industry_sector: payload.industry,
    website: payload.website,
    phone: payload.contactPhone,
    country: payload.country,
    city: payload.city,
    address: payload.headquarters,
    description: payload.description,
    contact_email: payload.contactEmail,
  });

  return response.data;
};

/* ===========================
   UPDATE COMPANY + REFRESH
=========================== */
export const updateOEMCompanyAndRefresh = async (payload: {
  companyName: string;
  industry: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
  country: string;
  city: string;
  headquarters: string;
  description: string;
}): Promise<OEMProfile | null> => {
  await updateOEMCompany(payload);
  return await fetchCurrentOEMProfile();
};

/* ===========================
   UPDATE INDUSTRIAL
=========================== */
export const updateOEMIndustrial = async (payload: {
  plants: number;
  assemblyLines: number;
  productionCapacity: number;
  sopPrograms: number;
}): Promise<any> => {
  const user_id = getCurrentUserId();

  if (!user_id) {
    throw new Error("id_user introuvable dans localStorage");
  }

  const response = await axiosInstance.post("/oems/update-industrial", {
    user_id,
    plants: payload.plants,
    assembly_lines: payload.assemblyLines,
    production_capacity: payload.productionCapacity,
    sop_programs: payload.sopPrograms,
  });

  return response.data;
};

/* ===========================
   UPDATE INDUSTRIAL + REFRESH
=========================== */
export const updateOEMIndustrialAndRefresh = async (payload: {
  plants: number;
  assemblyLines: number;
  productionCapacity: number;
  sopPrograms: number;
}): Promise<OEMProfile | null> => {
  await updateOEMIndustrial(payload);
  return await fetchCurrentOEMProfile();
};

/* ===========================
   UPDATE PROCUREMENT
=========================== */
export const updateOEMProcurement = async (payload: {
  procurementSegments: string[];
  localContentTarget: number;
}): Promise<any> => {
  const user_id = getCurrentUserId();

  if (!user_id) {
    throw new Error("id_user introuvable dans localStorage");
  }

  const response = await axiosInstance.post("/oems/update-procurement", {
    user_id,
    procurement_segments: payload.procurementSegments,
    local_content_target: `${payload.localContentTarget}%`,
  });

  return response.data;
};

/* ===========================
   UPDATE PROCUREMENT + REFRESH
=========================== */
export const updateOEMProcurementAndRefresh = async (payload: {
  procurementSegments: string[];
  localContentTarget: number;
}): Promise<OEMProfile | null> => {
  await updateOEMProcurement(payload);
  return await fetchCurrentOEMProfile();
};

/* ===========================
   UPLOAD OEM LOGO
=========================== */
export const uploadOEMLogo = async (file: File): Promise<any> => {
  const user_id = getCurrentUserId();

  if (!user_id) {
    throw new Error("id_user introuvable");
  }

  const formData = new FormData();
  formData.append("logo", file);
  formData.append("user_id", String(user_id));

  const response = await axiosInstance.post(
    "/oems/update-profile",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

/* ===========================
   UPLOAD OEM LOGO + REFRESH
=========================== */
export const uploadOEMLogoAndRefresh = async (
  file: File
): Promise<OEMProfile | null> => {
  await uploadOEMLogo(file);
  return await fetchCurrentOEMProfile();
};

export default axiosInstance;